import MailEvent from '@/utils/domain/constants/enumerator/MailEvent';

/**
 * Constants for mail event mappings
 */
const MAIL_EVENT_MAP = Object.freeze({
  0: 'processed',
  1: 'dropped',
  2: 'delivered',
  3: 'bounce',
  4: 'deferred',
  5: 'open',
  6: 'click',
  7: 'unsubscribe',
  8: 'resubscribe',
  9: 'spamReport',
});

/**
 * Default values for fallback scenarios
 */
const DEFAULTS = Object.freeze({
  COLOR: 'green',
  STATUS: {
    color: 'default',
    statusText: 'applications.text.email.status.unknown',
  },
});

/**
 * Utility functions for mail events
 */
export const MailEventUtils = {
  /**
   * Get color by mailEvent number
   * @param {number} mailEvent - The mail event number
   * @returns {string} The color for the event
   */
  getColorByEvent: (mailEvent) => {
    if (mailEvent === null || mailEvent === undefined) {
      return DEFAULTS.COLOR;
    }

    const eventId = MAIL_EVENT_MAP[mailEvent];
    if (!eventId) {
      return DEFAULTS.COLOR;
    }

    const eventConfig = Object.values(MailEventConfig).find((config) => config.id === eventId);

    return eventConfig?.color || DEFAULTS.COLOR;
  },

  /**
   * Get translation key by mailEvent number
   * @param {number} mailEvent - The mail event number
   * @returns {string} The translation key for the event
   */
  getTranslationKeyByEvent: (mailEvent) => {
    if (mailEvent === null || mailEvent === undefined) {
      return 'applications.text.emailEvent.unknown';
    }

    const eventId = MAIL_EVENT_MAP[mailEvent];
    if (!eventId) {
      return 'applications.text.emailEvent.unknown';
    }

    return `applications.text.emailEvent.${eventId}`;
  },

  /**
   * Get all options for dropdowns
   * @returns {Array} Array of options with value and label
   */
  getEventOptions: () => {
    return Object.values(MailEventConfig).map((event) => ({
      value: event.value,
      label: event.translationKey,
    }));
  },

  /**
   * Get status configuration by response code
   * @param {string|number} responseCode - The HTTP response code
   * @returns {Object} Status configuration with color and statusText
   */
  getStatusByResponseCode: (responseCode) => {
    if (!responseCode) {
      return DEFAULTS.STATUS;
    }

    const code = parseInt(responseCode, 10);
    if (isNaN(code)) {
      return DEFAULTS.STATUS;
    }

    return EmailStatusConfig[code] || DEFAULTS.STATUS;
  },

  /**
   * Check if a mail event is valid
   * @param {number} mailEvent - The mail event number
   * @returns {boolean} True if valid, false otherwise
   */
  isValidMailEvent: (mailEvent) => {
    return (
      mailEvent !== null && mailEvent !== undefined && MAIL_EVENT_MAP.hasOwnProperty(mailEvent)
    );
  },
};

/**
 * Mail Events Configuration
 * Combines color mapping and translation keys for email events
 */
export const MailEventConfig = Object.freeze({
  [MailEvent.Processed]: {
    id: 'processed',
    color: 'green',
    translationKey: 'applications.text.emailEvent.processed',
    value: 'Processed',
  },
  [MailEvent.Dropped]: {
    id: 'dropped',
    color: 'red',
    translationKey: 'applications.text.emailEvent.dropped',
    value: 'Dropped',
  },
  [MailEvent.Delivered]: {
    id: 'delivered',
    color: 'green',
    translationKey: 'applications.text.emailEvent.delivered',
    value: 'Delivered',
  },
  [MailEvent.Bounce]: {
    id: 'bounce',
    color: 'red',
    translationKey: 'applications.text.emailEvent.bounce',
    value: 'Bounce',
  },
  [MailEvent.Deferred]: {
    id: 'deferred',
    color: 'yellow',
    translationKey: 'applications.text.emailEvent.deferred',
    value: 'Deferred',
  },
  [MailEvent.Open]: {
    id: 'open',
    color: 'green',
    translationKey: 'applications.text.emailEvent.open',
    value: 'Open',
  },
  [MailEvent.Click]: {
    id: 'click',
    color: 'green',
    translationKey: 'applications.text.emailEvent.click',
    value: 'Click',
  },
  [MailEvent.Unsubscribe]: {
    id: 'unsubscribe',
    color: 'red',
    translationKey: 'applications.text.emailEvent.unsubscribe',
    value: 'Unsubscribe',
  },
  [MailEvent.Resubscribe]: {
    id: 'resubscribe',
    color: 'green',
    translationKey: 'applications.text.emailEvent.resubscribe',
    value: 'Resubscribe',
  },
  [MailEvent.SpamReport]: {
    id: 'spamReport',
    color: 'red',
    translationKey: 'applications.text.emailEvent.spamReport',
    value: 'SpamReport',
  },
});

/**
 * Email Message Types Configuration
 */
export const EmailMessageTypes = Object.freeze([
  {
    value: 'Unknown',
    label: 'applications.text.emailType.unknown',
  },
  {
    value: 'ProcessInstancePendingApproval',
    label: 'applications.text.emailType.processInstancePendingApproval',
  },
  {
    value: 'ProcessInstancePendingCompleteInformation',
    label: 'applications.text.emailType.processInstancePendingCompleteInformation',
  },
  {
    value: 'ProcessInstancePendingTaskCompletion',
    label: 'applications.text.emailType.processInstancePendingTaskCompletion',
  },
  {
    value: 'ProcessInstancePendingSignature',
    label: 'applications.text.emailType.processInstancePendingSignature',
  },
  {
    value: 'ProcessInstanceApprovalSecurityCode',
    label: 'applications.text.emailType.processInstanceApprovalSecurityCode',
  },
  {
    value: 'ProcessInstanceActionSendEmail',
    label: 'applications.text.emailType.processInstanceActionSendEmail',
  },
  {
    value: 'ProcessInstanceNewCommentNotification',
    label: 'applications.text.emailType.processInstanceNewCommentNotification',
  },
  {
    value: 'ProcessInstanceScheduledExecutionNotification',
    label: 'applications.text.emailType.processInstanceScheduledExecutionNotification',
  },
  {
    value: 'ProcessInstanceApprovedNotification',
    label: 'applications.text.emailType.processInstanceApprovedNotification',
  },
  {
    value: 'ProcessInstanceRejectedNotification',
    label: 'applications.text.emailType.processInstanceRejectedNotification',
  },
  {
    value: 'ProcessInstanceCanceledNotification',
    label: 'applications.text.emailType.processInstanceCanceledNotification',
  },
  {
    value: 'ProcessInstanceVoidedNotification',
    label: 'applications.text.emailType.processInstanceVoidedNotification',
  },
  {
    value: 'ProcessInstanceWithErrorsNotification',
    label: 'applications.text.emailType.processInstanceWithErrorsNotification',
  },
  {
    value: 'UserAccountConfirmation',
    label: 'applications.text.emailType.userAccountConfirmation',
  },
  {
    value: 'UserAccountWelcomeMessage',
    label: 'applications.text.emailType.userAccountWelcomeMessage',
  },
  {
    value: 'UserAccountResetPassword',
    label: 'applications.text.emailType.userAccountResetPassword',
  },
  {
    value: 'UserAccountTwoFactorValidationCode',
    label: 'applications.text.emailType.userAccountTwoFactorValidationCode',
  },
  {
    value: 'UserAccountSecurityAlertNewLogin',
    label: 'applications.text.emailType.userAccountSecurityAlertNewLogin',
  },
  {
    value: 'InternalTaskErrorNotification',
    label: 'applications.text.emailType.internalTaskErrorNotification',
  },
  {
    value: 'InternalTaskEntityFileReadyToDownloadNotification',
    label: 'applications.text.emailType.internalTaskEntityFileReadyToDownloadNotification',
  },
  {
    value: 'InternalTaskEntityFileImportFinishNotification',
    label: 'applications.text.emailType.internalTaskEntityFileImportFinishNotification',
  },
  {
    value: 'FormApiFieldApiNotificationWithErrors',
    label: 'applications.text.emailType.formApiFieldApiNotificationWithErrors',
  },
  {
    value: 'MaintenanceCompanyDeactivationNotice',
    label: 'applications.text.emailType.maintenanceCompanyDeactivationNotice',
  },
  {
    value: 'RequestTaskReassigned',
    label: 'applications.text.emailType.requestTaskReassigned',
  },
  {
    value: 'InternalTaskNotificationCompleted',
    label: 'applications.text.emailType.internalTaskNotificationCompleted',
  },
  {
    value: 'AssignedTask',
    label: 'applications.text.emailType.assignedTask',
  },
  {
    value: 'InternalTaskDynamicEntityImportCompleted',
    label: 'applications.text.emailType.internalTaskDynamicEntityImportCompleted',
  },
]);

/**
 * Email Status Configuration
 * Maps HTTP response codes to status configurations
 */
export const EmailStatusConfig = Object.freeze({
  202: {
    color: 'approved',
    statusText: 'applications.text.email.status.sent',
  },
  400: {
    color: 'rejected',
    statusText: 'applications.text.email.status.badRequest',
  },
  401: {
    color: 'rejected',
    statusText: 'applications.text.email.status.unauthorized',
  },
  403: {
    color: 'rejected',
    statusText: 'applications.text.email.status.forbidden',
  },
  404: {
    color: 'rejected',
    statusText: 'applications.text.email.status.notFound',
  },
  413: {
    color: 'rejected',
    statusText: 'applications.text.email.status.payloadTooLarge',
  },
  429: {
    color: 'rejected',
    statusText: 'applications.text.email.status.tooManyRequests',
  },
  500: {
    color: 'execution',
    statusText: 'applications.text.email.status.internalServerError',
  },
  502: {
    color: 'execution',
    statusText: 'applications.text.email.status.badGateway',
  },
  503: {
    color: 'execution',
    statusText: 'applications.text.email.status.serviceUnavailable',
  },
  504: {
    color: 'execution',
    statusText: 'applications.text.email.status.gatewayTimeout',
  },
});
